import {
  FormCandidateModelFragment,
  FormType,
  StageCandidateStatus,
  useUserPageGetProjectByIdQuery,
} from 'generated/graphql';
import { FC, useMemo } from 'react';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import ActionsResultTable from './ActionsResultTable/ActionsResultTable';
import StageManagement from './StageReset/StageManagement';
import StageResult, {
  StageResultHeader,
  tagContent,
} from './StageResult/StageResult';
import styles from './UserReset.module.scss';
import { IUserReset } from './UserResets.interface';

const UserReset: FC<IUserReset> = ({
  userInfo,
  stageId,
  stageName,
  stageStatus,
  projectId,
  lastDateOfCandidateAction,
  scores,
  stageCandidateId,
  stageCandidateSubId,
  rssAssessmentCandidates,
  formCandidates,
  deiForm,
  filteredTestActionCandidates,
  projectCandidate,
  isCardOpen,
  setIsCardOpen,
}) => {
  const { handleMsgType } = useNotification();

  const { data: getProjectByIdResponse, loading: getProjectByIdLoading } =
    useUserPageGetProjectByIdQuery({
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
      },
      variables: {
        id: projectId,
      },
      fetchPolicy: 'cache-first',
    });
  const project = getProjectByIdResponse?.project;
  const company = project?.company;

  const candidateName =
    userInfo?.firstName && userInfo?.lastName
      ? `${userInfo?.firstName} ${userInfo?.lastName}`
      : '-';

  const stageStatusTag = Boolean(projectCandidate?.isExpired)
    ? 'EXPIRED'
    : [
      StageCandidateStatus.Completed,
      StageCandidateStatus.InProgress,
      StageCandidateStatus.NotStarted,
    ].includes(stageStatus || StageCandidateStatus.NotStarted)
      ? stageStatus
      : undefined;

  const rsForm = useMemo(() => {
    return formCandidates?.find(
      (fc: FormCandidateModelFragment) => fc.form.formType === FormType.RsForm
    );
  }, [formCandidates]);

  if (getProjectByIdLoading) {
    return <Loader variant="spinner" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <StageResultHeader
          companyName={company?.name}
          companyId={company?.id}
          projectName={project?.name}
          projectId={project?.id}
          setIsCardOpen={setIsCardOpen}
          isCardOpen={isCardOpen}
          stageStatus={stageStatusTag as keyof typeof tagContent}
        />
      </div>

      {isCardOpen && (
        <>
          <StageResult
            companyId={company?.id}
            projectName={project?.name}
            projectId={project?.id}
            projectSubId={project?.subId}
            stageName={stageName}
            stageId={stageId}
            stageCandidateId={stageCandidateId}
            stageCandidateSubId={stageCandidateSubId}
            stageStatus={stageStatus}
            lastDateOfCandidateAction={lastDateOfCandidateAction}
            projectCandidate={projectCandidate}
            candidateName={candidateName}
            candidateId={userInfo?.id}
            companyName={company?.name}
            userSubId={userInfo?.subId}
          />
          <ActionsResultTable
            projectName={project?.name}
            userInfo={userInfo}
            stageId={stageId}
            stageStatus={stageStatus}
            filteredTestActionCandidates={filteredTestActionCandidates}
            rssAssessmentCandidates={rssAssessmentCandidates}
            stageCandidateId={stageCandidateId}
            rsForm={rsForm}
            projectCandidate={projectCandidate}
            deiForm={deiForm}
          />
          <StageManagement
            userInfo={userInfo}
            companyId={company?.id || 0}
            companyName={company?.name || ''}
            stageId={stageId}
            projectId={projectId}
            stageStatus={stageStatus}
            stageCandidateId={stageCandidateId}
          />
        </>
      )}
    </div>
  );
};

export default UserReset;
